/**
 * Serial number checker for spare part product pages.
 *
 * The matching rules mirror web/backend/lib/spare-parts/spare-part-matcher.js in the
 * aduro-warehouse-and-pricing app. Keep both implementations in sync.
 *
 * Range line grammar: [PREFIX ' '] START '-' END, e.g. "0-13063750", "VA 0-13063750",
 * "0000N0000-9999N9999". Matching is strict: the prefix and any letters inside the number must
 * be identical to the range.
 */
if (!customElements.get('serial-number-checker')) {
  const RANGE_LINE_PATTERN = /^(?:([A-Z]+) )?([0-9A-Z]+)-([0-9A-Z]+)$/;
  const SERIAL_PATTERN = /^([A-Z]*)([0-9][0-9A-Z]*)$/;

  const analyzeSerialBody = (body) => {
    const digits = body.replace(/[A-Z]/g, '');
    if (!digits) return null;
    return {
      letters: body.replace(/[0-9]/g, ''),
      groupLengths: body.split(/[A-Z]+/).map((group) => group.length),
      value: BigInt(digits)
    };
  };

  const parseRangeLine = (line) => {
    const normalized = String(line ?? '').trim().toUpperCase().replace(/\s+/g, ' ');
    const match = RANGE_LINE_PATTERN.exec(normalized);
    if (!match) return null;
    const [, prefix = '', start, end] = match;
    const startShape = analyzeSerialBody(start);
    const endShape = analyzeSerialBody(end);
    if (!startShape || !endShape) return null;
    if (startShape.letters !== endShape.letters) return null;
    if (startShape.value > endShape.value) return null;
    return {
      prefix,
      letters: startShape.letters,
      groupLengths: startShape.groupLengths,
      min: startShape.value,
      max: endShape.value
    };
  };

  const parseSerial = (input) => {
    const normalized = String(input ?? '').toUpperCase().replace(/[\s\-._]/g, '');
    const match = SERIAL_PATTERN.exec(normalized);
    if (!match) return null;
    const [, prefix, body] = match;
    const shape = analyzeSerialBody(body);
    if (!shape) return null;
    return { prefix, body, ...shape };
  };

  const sameGroupLengths = (a, b) => a.length === b.length && a.every((length, index) => length === b[index]);

  const serialMatchesRange = (serial, range) => {
    if (serial.prefix !== range.prefix) return false;
    if (serial.letters !== range.letters) return false;
    if (range.letters && !sameGroupLengths(serial.groupLengths, range.groupLengths)) return false;
    return serial.value >= range.min && serial.value <= range.max;
  };

  const serialMatchesAnyLine = (serial, rangeLines) =>
    (rangeLines || []).some((line) => {
      const range = parseRangeLine(line);
      return range !== null && serialMatchesRange(serial, range);
    });

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    })[character]);

  const interpolate = (template, values) =>
    String(template ?? '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => (key in values ? values[key] : ''));

  const STORAGE_KEY = 'aduro-serial-checker';
  const STATUS_CLASSES = { fits: 'banner--success', alternative: 'banner--error', unknown: 'banner--warning' };

  const loadSavedCheck = () => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
      return saved && typeof saved.model === 'string' && typeof saved.serial === 'string' ? saved : null;
    } catch {
      return null;
    }
  };

  const saveCheck = (model, serial) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ model, serial }));
    } catch {}
  };

  class SerialNumberChecker extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      try {
        this.data = JSON.parse(this.querySelector('[data-serial-checker-data]').textContent);
      } catch {
        return;
      }

      this.modelSelect = this.querySelector('select[name="serial-checker-model"]');
      this.serialInput = this.querySelector('input[name="serial-checker-serial"]');
      this.submitButton = this.querySelector('.serial-checker__submit');
      this.results = {};
      this.querySelectorAll('[data-serial-result]').forEach((element) => {
        this.results[element.dataset.serialResult] = element;
      });

      if (!this.modelSelect || !this.serialInput || !this.submitButton) return;

      this.ownFitments = (this.data.fitments || []).filter((fitment) => fitment.productId === this.data.productId);
      this.statusElement = document.querySelector(`[data-serial-checker-status="${this.dataset.statusId}"]`);

      this._abortController = new AbortController();
      const { signal } = this._abortController;

      this._populateModels();
      this._restoreSavedCheck();

      this.submitButton.addEventListener('click', () => this._check(), { signal });
      this.serialInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this._check();
        }
      }, { signal });
      this.modelSelect.addEventListener('change', () => this._hideResults(), { signal });
      this.serialInput.addEventListener('input', () => this._hideResults(), { signal });
    }

    disconnectedCallback() {
      if (this._abortController) {
        this._abortController.abort();
        this._abortController = null;
      }
      this._initialized = false;
    }

    _populateModels() {
      if (this.modelSelect.querySelectorAll('option:not([disabled])').length > 0) return;

      const models = new Map();
      for (const fitment of this.ownFitments) {
        if (fitment.model?.handle && !models.has(fitment.model.handle)) {
          models.set(fitment.model.handle, fitment.model.name || fitment.model.handle);
        }
      }

      [...models.entries()]
        .sort((a, b) => a[1].localeCompare(b[1], undefined, { numeric: true }))
        .forEach(([handle, name]) => {
          const option = document.createElement('option');
          option.value = handle;
          option.textContent = name;
          this.modelSelect.appendChild(option);
        });

      if (models.size === 1) {
        this.modelSelect.value = [...models.keys()][0];
      } else if (this.data.preselectModel && models.has(this.data.preselectModel)) {
        this.modelSelect.value = this.data.preselectModel;
      }
    }

    _restoreSavedCheck() {
      const saved = loadSavedCheck();
      if (!saved) return;

      this.serialInput.value = saved.serial;

      const hasModel = [...this.modelSelect.options].some((option) => option.value === saved.model);
      if (!hasModel) return;

      this.modelSelect.value = saved.model;
      this._check();
    }

    _check() {
      this._hideResults();

      const modelHandle = this.modelSelect.value;
      if (!modelHandle) {
        this._showResult('error', escapeHtml(this.data.strings.errorSelectModel));
        return;
      }

      const serial = parseSerial(this.serialInput.value);
      if (!serial) {
        this._showResult('error', escapeHtml(this.data.strings.errorEmpty));
        return;
      }

      saveCheck(modelHandle, this.serialInput.value.trim());

      const modelName = this.modelSelect.selectedOptions[0]?.textContent || '';
      const fitsOwn = this.ownFitments
        .filter((fitment) => fitment.model.handle === modelHandle)
        .some((fitment) => serialMatchesAnyLine(serial, fitment.ranges));

      if (fitsOwn) {
        this._showResult('fits', escapeHtml(interpolate(this.data.strings.fits, { model: modelName })));
        return;
      }

      const alternative = (this.data.fitments || []).find(
        (fitment) =>
          fitment.productId !== this.data.productId &&
          fitment.model.handle === modelHandle &&
          serialMatchesAnyLine(serial, fitment.ranges)
      );

      if (alternative) {
        this._showResult(
          'alternative',
          interpolate(this.data.strings.alternativeHtml, {
            model: escapeHtml(modelName),
            url: escapeHtml(alternative.productUrl),
            title: escapeHtml(alternative.productTitle)
          })
        );
        return;
      }

      this._showResult(
        'unknown',
        interpolate(this.data.strings.unknownHtml, {
          model: escapeHtml(modelName),
          email: escapeHtml(this.data.serviceEmail)
        })
      );
    }

    _showResult(type, html) {
      const element = this.results[type];
      if (element) {
        element.querySelector('.banner__content').innerHTML = html;
        element.hidden = false;
      }

      if (this.statusElement && STATUS_CLASSES[type]) {
        this.statusElement.classList.remove(...Object.values(STATUS_CLASSES));
        this.statusElement.classList.add(STATUS_CLASSES[type]);
        this.statusElement.dataset.serialResult = type;
        this.statusElement.querySelector('.banner__content').innerHTML = html;
        this.statusElement.hidden = false;
      }
    }

    _hideResults() {
      Object.values(this.results).forEach((element) => {
        element.hidden = true;
      });
    }
  }

  customElements.define('serial-number-checker', SerialNumberChecker);
}
